import { HttpParams } from '@angular/common/http'
import { ParamMap } from '@angular/router'

export type SortDirection = 'asc' | 'desc'
export type ListParameterQuery = {
  page: number
  pageSize: number
  sortField?: string
  sortDirection?: SortDirection
}

export type ListParameterQueryOptional = Partial<ListParameterQuery>

/** The shape every `ListQueryResponseDto` endpoint returns. */
export type ListQueryResponse<T> = { list: T[]; total: number }

/**
 * One column's filter, in the shape its mode sends. A value set is a facet, `contains` is a text
 * match and `from`/`to` is a resolved date range — date presets become absolute ISO instants here so
 * the server contract stays generic.
 */
export type ListFilterValues = { values: (string | number)[] } | { contains: string } | { from: string | null; to: string | null }

type ListFilterSuffix = 'from' | 'to' | 'contains'

/**
 * Filters travel as flat query keys because Fastify's default parser does not decode `filter[x]=`
 * into nested objects. The suffix picks the mode the server reads them as.
 */
function constructFilterParameterName(field: string, suffix?: ListFilterSuffix): string {
  return suffix === undefined ? `filter.${field}` : `filter.${field}.${suffix}`
}

function appendFilterParameter(params: HttpParams, field: string, filter: ListFilterValues): HttpParams {
  if ('values' in filter) {
    return filter.values.reduce((next, value) => next.append(constructFilterParameterName(field), String(value)), params)
  }
  if ('contains' in filter) {
    return filter.contains.length === 0 ? params : params.set(constructFilterParameterName(field, 'contains'), filter.contains)
  }
  return (['from', 'to'] as const).reduce((next, suffix) => {
    const value = filter[suffix]
    return value === null ? next : next.set(constructFilterParameterName(field, suffix), value)
  }, params)
}

/** A query parameter's value. A list becomes one `key=value` pair per entry, as HttpParams writes it. */
export type ListParameterValue = string | number | boolean | readonly (string | number | boolean)[]

export function toListHttpParams(
  query: ListParameterQuery,
  filters: Record<string, ListFilterValues>,
  extra: Record<string, ListParameterValue> = {}
): HttpParams {
  const sort: Record<string, string> =
    query.sortField !== undefined && query.sortDirection !== undefined ? { sortField: query.sortField, sortDirection: query.sortDirection } : {}
  const params = new HttpParams({ fromObject: { page: query.page, pageSize: query.pageSize, ...sort, ...extra } })
  return Object.entries(filters).reduce((next, [field, filter]) => appendFilterParameter(next, field, filter), params)
}

export function constructQueryParameterName(key: string, prefix: string | null, postFix: string | null = null): string {
  if (prefix === null) {
    return key
  }
  return `${prefix}${key.charAt(0).toUpperCase()}${key.slice(1)}${postFix !== null ? `.${postFix}` : ''}`
}

// TODO properly type and handle undefined/null values
export function getPaginationOptionsFromQueryParamMap(queryParamMap: ParamMap, prefix: string | null = null, defaults: ListParameterQueryOptional = {}) {
  const page = queryParamMap.has(constructQueryParameterName('page', prefix))
    ? parseInt(queryParamMap.get(constructQueryParameterName('page', prefix))!)
    : (defaults?.page ?? 0)
  const pageSize = parseInt(queryParamMap.get(constructQueryParameterName('pageSize', prefix)) ?? defaults?.pageSize?.toString() ?? '100')
  const sortField = queryParamMap.get(constructQueryParameterName('sortField', prefix)) ?? defaults?.sortField ?? null
  const sortDirection = queryParamMap.get(constructQueryParameterName('sortDirection', prefix)) ?? (defaults?.sortDirection as SortDirection) ?? null
  return {
    page,
    pageSize,
    sortField,
    sortDirection,
  }
}
